import React, { useEffect, useState } from 'react';
import { ChevronRight, ScrollText } from 'lucide-react';
import { PickupEstimateMeta, DropoffEstimateMeta } from './TripEstimateInline';
import CompanyNoteIconButton, { getCompanyNote } from './CompanyNoteIconButton';
import { DriverFreezeTypeBadge, DriverMultiloadBadge } from './DriverFreezeTypeBadge';
import { sortTripsForDisplay, buildTripSections } from '../utils/tripSections';
import { getMultiloadGroupP1Trip, calculateMultiloadGroupFreeTimeInBucket, calculateTripFreeTimeInBucket, formatFreeTimeMinutes } from '../utils/tripFreeTime';
import { sumDeadheadMilesForTotal } from '../utils/deadheadEta';
import { tripMatchesTripsPageSearch } from '../utils/tripPageSearch';
import { clientNameWithLeg } from '../utils/groupLeg';
import { formatTripRevenue, sumTripRevenue } from '../utils/tripRevenue';
import {
  formatDriveMinutes,
  formatFleetClock,
  groupTripsByWindow,
  isFleetDriver,
  lastWindowAwayMiles,
  windowGarageReturn,
} from '../utils/fleetWindows';

const COLS = 11;
const HEADERS = ['#', 'Trip ID', 'Client', 'Pickup', 'Dropoff', 'LOS', 'Mi', '$', 'Note', 'Logs', 'Flags'];

function usePrefersReducedMotion() {
  const [reduce, setReduce] = useState(() => (
    typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  ));

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = () => setReduce(media.matches);
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, []);

  return reduce;
}

/** Fade the open bucket. Close drops it immediately so the list stays compact. */
function BucketPanel({ open, children }) {
  const reduce = usePrefersReducedMotion();
  const [present, setPresent] = useState(open);
  const [shown, setShown] = useState(open);

  useEffect(() => {
    if (open) {
      setPresent(true);
      if (reduce) {
        setShown(true);
        return undefined;
      }
      const frame = requestAnimationFrame(() => setShown(true));
      return () => cancelAnimationFrame(frame);
    }
    setShown(false);
    if (reduce) {
      setPresent(false);
      return undefined;
    }
    const timer = setTimeout(() => setPresent(false), 160);
    return () => clearTimeout(timer);
  }, [open, reduce]);

  if (reduce) {
    if (!open) return null;
    return <div className="border-t border-white/10 bg-white">{children}</div>;
  }

  if (!present) return null;

  return (
    <div className={`border-t border-white/10 bg-white ${shown ? 'trip-bucket-rows is-open' : 'trip-bucket-rows'}`}>
      {children}
    </div>
  );
}

export function TripLayoutSwitch({ value, onChange }) {
  const list = value === 'list';
  return (
    <div
      className="relative inline-grid grid-cols-2 shrink-0 rounded-lg bg-black/[0.05] p-0.5"
      role="tablist"
      aria-label="Trip layout"
    >
      <span
        aria-hidden
        className="pointer-events-none absolute top-0.5 bottom-0.5 left-0.5 w-[calc(50%-2px)] rounded-md bg-white shadow-[0_1px_2px_rgba(0,0,0,0.06),0_0_0_0.5px_rgba(0,0,0,0.04)] transition-transform duration-200 ease-out motion-reduce:transition-none"
        style={{ transform: list ? 'translateX(100%)' : 'translateX(0)' }}
      />
      {['board', 'list'].map((id) => {
        const selected = value === id;
        const label = id === 'board' ? 'Board' : 'List';
        return (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={selected}
            onClick={() => onChange(id)}
            className={`relative z-10 h-7 rounded-md px-3 text-[12px] font-medium tracking-[-0.01em] active:scale-[0.97] transition-transform duration-100 ease-out motion-reduce:transition-none motion-reduce:active:scale-100 ${
              selected ? 'text-[#1d1d1f]' : 'text-[#6e6e73] hover:text-[#1d1d1f]'
            }`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

function clock(iso, timezone) {
  return formatFleetClock(iso, timezone) || '—';
}

function scheduleParts(iso, timezone) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return {
    time: date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: timezone,
    }),
    day: date.toLocaleDateString('en-US', {
      month: '2-digit',
      day: '2-digit',
      timeZone: timezone,
    }),
  };
}

function losLabel(trip, spaceTypeMap) {
  if (trip.losTitle && trip.spaceTypeTitle) return `${trip.losTitle} (${trip.spaceTypeTitle})`;
  if (trip.los_title && trip.space_type_title) return `${trip.los_title} (${trip.space_type_title})`;
  const mapped = spaceTypeMap?.[trip.space_type];
  if (mapped) return `${mapped.title} (${mapped.original_title || mapped.title})`;
  return trip.los_title || trip.space_type || '—';
}

function tripBadges(trip, groupSize, groupIndex) {
  const badges = [];
  const add = (text, className, title) => badges.push({ text, className, title: title || text });
  const preferred = trip.preferred_driver_info?.preferred_driver_name;
  if (preferred) add(`Pref: ${preferred}`, 'bg-emerald-600', "Client's preferred driver");
  if (trip.driver_violation === true || trip.preferred_driver_violation === true) {
    add('Driver Violation', 'bg-rose-600', 'Assignment violates preference or constraints');
  }
  if (trip.account_type) add(String(trip.account_type), 'bg-main-dark');
  const escorts = Number(trip.no_of_escorts ?? trip.noOfEscorts ?? 0) || 0;
  if (escorts > 0) add(String(escorts), 'bg-purple-600', `${escorts} escort${escorts === 1 ? '' : 's'}`);
  const status = (trip.trip_status || '').toLowerCase();
  if (status === 'onway' || status === 'on way') add('On Way', 'bg-blue-500');
  else if (status === 'arrived') add('On Scene', 'bg-orange-500');
  else if (status === 'started' || status === 'pickedup') add('Picked Up', 'bg-emerald-500');
  else if (trip.is_busy_trip && trip.trip_status) add(String(trip.trip_status), 'bg-sky-500');
  const isMultiload = trip.is_multiload === true;
  const size = groupSize ?? ((trip.multiload_partner_ids?.length || 0) + 1);
  const position = groupIndex ?? trip.multiload_pickup_order;
  if (isMultiload && size > 1 && position) add(`${position}/${size}`, 'bg-main-primary', 'Multiload position');
  if (trip.manually_adjusted === true) add('Manual', 'bg-amber-500', 'Manually adjusted');
  if (trip.new_assignment === true) add('New', 'bg-violet-600', 'Newly assigned in this run');
  if (trip.assumedWillCallTime === true || trip.assumed_will_call_time === true) {
    add('WC', 'bg-sky-600', 'Will-call time assumed from historical pattern');
  }
  if (!trip.is_break && trip.sequence != null) add(`P(${trip.sequence})`, 'bg-yellow-700');
  return badges;
}

function freeLabel(result) {
  if (!result || result.freeMinutes == null) return '';
  return `${formatFreeTimeMinutes(result.freeMinutes)} free`;
}

function haversineMiles(lat1, lng1, lat2, lng2) {
  const a1 = Number(lat1);
  const o1 = Number(lng1);
  const a2 = Number(lat2);
  const o2 = Number(lng2);
  if (![a1, o1, a2, o2].every(Number.isFinite)) return 0;
  const R = 3958.8;
  const dLat = ((a2 - a1) * Math.PI) / 180;
  const dLng = ((o2 - o1) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos((a1 * Math.PI) / 180) * Math.cos((a2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

function awayMiles(driver) {
  if (isFleetDriver(driver)) return lastWindowAwayMiles(driver);
  const sorted = [...(driver.trips || [])].sort((a, b) => (a.sequence ?? 999) - (b.sequence ?? 999));
  const last = [...sorted].reverse().find((trip) => !trip.is_break);
  if (!last) return 0;
  const dropLat = last.dropoff_latitude || last.dropoff_lat;
  const dropLng = last.dropoff_longitude || last.dropoff_lng;
  let homeLat;
  let homeLng;
  if (Array.isArray(driver.takeMeHome) && driver.takeMeHome.length >= 2) {
    homeLng = driver.takeMeHome[0];
    homeLat = driver.takeMeHome[1];
  } else {
    homeLat = driver.ref_latitude ?? driver.current_latitude ?? driver.driver_latitude ?? driver.latitude;
    homeLng = driver.ref_longitude ?? driver.current_longitude ?? driver.driver_longitude ?? driver.longitude;
  }
  return haversineMiles(dropLat, dropLng, homeLat, homeLng);
}

function driverStats(driver) {
  const trips = driver.trips || [];
  const tripCount = trips.filter((trip) => !trip.is_break).length;
  const loaded = trips.reduce(
    (sum, trip) => sum + (Number(trip.trip_distance_miles) || Number(trip.trip_distance) || 0),
    0
  );
  return {
    tripCount,
    loaded,
    deadhead: sumDeadheadMilesForTotal(trips),
    away: awayMiles(driver),
    revenue: sumTripRevenue(trips),
  };
}

function shiftLabel(driver, timezone) {
  const start = driver.shift_window?.shift_start;
  const end = driver.shift_window?.shift_end;
  if (!driver.shift_window?.has_shift || !start || !end) return '';
  return `${clock(start, timezone)}–${clock(end, timezone)}`;
}

function fleetCount(driver) {
  return (driver.fleet_windows || []).length;
}

function PlaceCell({ address, iso, timezone, estimate }) {
  const when = scheduleParts(iso, timezone);
  return (
    <div className="min-w-[280px] max-w-[420px]">
      <div className="whitespace-normal break-words text-[12px] leading-4 text-[#1d1d1f]" title={address || ''}>
        {address || '—'}
      </div>
      <div className="mt-0.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11px] leading-4 tabular-nums text-[#6e6e73]">
        <span>{when ? when.time : '—'}</span>
        {when ? <span>{when.day}</span> : null}
        {estimate}
      </div>
    </div>
  );
}

function FlagList({ badges }) {
  if (!badges.length) return <span className="text-[#c7c7cc]">—</span>;
  return (
    <div className="flex max-w-[240px] flex-wrap gap-1">
      {badges.map((badge) => (
        <span
          key={`${badge.className}-${badge.text}`}
          title={badge.title}
          className={`inline-flex items-center rounded-md px-1.5 py-0.5 text-[10px] font-medium leading-none text-white ${badge.className}`}
        >
          {badge.text}
        </span>
      ))}
    </div>
  );
}

const thClass = 'border-b border-black/[0.08] px-2 py-1.5 text-left text-[11px] font-medium tracking-[-0.006em] text-[#6e6e73] whitespace-nowrap';
const tdClass = 'border-b border-black/[0.06] px-2 py-1.5 align-top text-[12px] leading-4 text-[#1d1d1f]';

function groupClass(position) {
  if (!position) return '';
  if (position === 'only') return 'ml-group ml-only';
  if (position === 'first') return 'ml-group ml-first';
  if (position === 'last') return 'ml-group ml-last';
  return 'ml-group';
}

function legFacts(trip, driverTrips, multiloadRoutes, asGroup) {
  const miles = Number(trip?.deadhead_miles) || 0;
  const eta = trip?.is_busy_trip || trip?.sequence === 0
    ? trip?.available_in_minutes
    : trip?.eta_to_pickup_minutes;
  let free = '';
  try {
    const result = asGroup
      ? calculateMultiloadGroupFreeTimeInBucket(trip, driverTrips, multiloadRoutes)
      : calculateTripFreeTimeInBucket(trip, driverTrips, multiloadRoutes);
    free = freeLabel(result);
  } catch {
    free = '';
  }
  return { miles, eta, free };
}

function GapBars({ facts }) {
  const eta = Number(facts.eta);
  const hasEta = Number.isFinite(eta) && eta > 0;
  const hasDrive = facts.miles > 0 || hasEta;
  if (!facts.free && !hasDrive) return null;
  return (
    <>
      {facts.free ? (
        <tr>
          <td colSpan={COLS} className="border-b border-teal-200/80 bg-teal-50 px-3 py-1 text-center text-[11px] font-medium tabular-nums text-teal-800">
            {facts.free}
          </td>
        </tr>
      ) : null}
      {hasDrive ? (
        <tr>
          <td colSpan={COLS} className="border-b border-black/[0.06] bg-[#f5f5f7] px-3 py-1 text-center text-[11px] tabular-nums text-[#3a3a3c]">
            <span className="inline-flex items-center justify-center gap-5">
              {facts.miles > 0 ? (
                <span>
                  <span className="font-medium">DH</span>
                  <span className="ml-1">{facts.miles.toFixed(1)} mi</span>
                </span>
              ) : null}
              {hasEta ? (
                <span>
                  <span className="font-medium">ETD</span>
                  <span className="ml-1.5 font-medium">ETA</span>
                  <span className="ml-1">{Math.round(eta)} min</span>
                </span>
              ) : null}
            </span>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function TripDataRow({
  trip,
  num,
  timezone,
  spaceTypeMap,
  highlighted,
  onViewLogs,
  canViewLogs,
  groupPos,
  groupSize,
  groupIndex,
}) {
  const isBreak = trip.is_break === true;
  const tripId = trip.trip_id || trip.id || '';
  const note = getCompanyNote(trip);
  const rowClass = [
    groupClass(groupPos),
    highlighted ? 'bg-[#0071e3]/[0.06]' : '',
  ].filter(Boolean).join(' ');

  if (isBreak) {
    const minutes = Math.round(Number(trip.break_duration_minutes) || 0);
    const breakText = (value) => {
      if (!value) return '—';
      const raw = String(value);
      if (raw.indexOf('T') === -1) return raw;
      const parts = scheduleParts(value, timezone);
      return parts ? `${parts.time} ${parts.day}` : raw;
    };
    return (
      <tr className={rowClass || undefined}>
        <td className={`${tdClass} tabular-nums text-[#6e6e73]`}>{num}</td>
        <td className={`${tdClass} tabular-nums text-[#6e6e73]`}>{tripId || '—'}</td>
        <td className={tdClass}>{clientNameWithLeg(trip.client_name || 'Rest break', trip)}</td>
        <td className={tdClass}>{breakText(trip.break_start || trip.schedule_time)}</td>
        <td className={tdClass}>{breakText(trip.break_end || trip.appointment_time)}</td>
        <td className={`${tdClass} text-[#6e6e73]`}>{minutes ? `Break · ${minutes} min` : 'Break'}</td>
        <td className={`${tdClass} text-[#c7c7cc]`}>—</td>
        <td className={`${tdClass} text-[#c7c7cc]`}>—</td>
        <td className={tdClass}>{note ? <CompanyNoteIconButton companyNote={note} compact /> : <span className="text-[#c7c7cc]">—</span>}</td>
        <td className={tdClass} />
        <td className={tdClass}><FlagList badges={[{ text: 'Break', className: 'bg-teal-600', title: 'Break' }]} /></td>
      </tr>
    );
  }

  return (
    <tr className={`transition-colors ${rowClass} ${highlighted ? '' : 'hover:bg-black/[0.025]'}`}>
      <td className={`${tdClass} tabular-nums font-medium text-[#1d1d1f]`}>{num}</td>
      <td className={`${tdClass} max-w-[140px] truncate font-medium tabular-nums`} title={String(tripId)}>{tripId || '—'}</td>
      <td className={`${tdClass} max-w-[180px]`}>
        <div className="truncate font-medium" title={clientNameWithLeg(trip.client_name, trip)}>
          {clientNameWithLeg(trip.client_name || '—', trip)}
        </div>
      </td>
      <td className={tdClass}>
        <PlaceCell
          address={trip.pickup_address}
          iso={trip.schedule_time}
          timezone={timezone}
          estimate={<PickupEstimateMeta trip={trip} timezone={timezone} />}
        />
      </td>
      <td className={tdClass}>
        <PlaceCell
          address={trip.dropoff_address}
          iso={trip.appointment_time}
          timezone={timezone}
          estimate={<DropoffEstimateMeta trip={trip} timezone={timezone} />}
        />
      </td>
      <td className={`${tdClass} max-w-[160px]`}>
        <div className="truncate text-[#3a3a3c]" title={losLabel(trip, spaceTypeMap)}>{losLabel(trip, spaceTypeMap)}</div>
      </td>
      <td className={`${tdClass} tabular-nums whitespace-nowrap`}>{Number(trip.trip_distance_miles || 0).toFixed(1)}</td>
      <td className={`${tdClass} tabular-nums whitespace-nowrap font-medium`}>{formatTripRevenue(trip)}</td>
      <td className={tdClass}>
        {note ? <CompanyNoteIconButton companyNote={note} compact /> : <span className="text-[#c7c7cc]">—</span>}
      </td>
      <td className={tdClass}>
        <button
          type="button"
          disabled={!canViewLogs || !tripId}
          onClick={() => onViewLogs(String(tripId))}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-main-primary active:scale-[0.97] transition-transform duration-100 ease-out disabled:text-[#c7c7cc] disabled:active:scale-100 motion-reduce:transition-none"
        >
          <ScrollText className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
          Logs
        </button>
      </td>
      <td className={tdClass}><FlagList badges={tripBadges(trip, groupSize, groupIndex)} /></td>
    </tr>
  );
}

function sectionRows({
  sections,
  timezone,
  spaceTypeMap,
  driverTrips,
  multiloadRoutes,
  searchTerm,
  onViewLogs,
  canViewLogs,
}) {
  const rows = [];
  let count = 0;

  const pushLeg = (key, trip, asGroup) => {
    const facts = legFacts(trip, driverTrips, multiloadRoutes, asGroup);
    rows.push(<GapBars key={key} facts={facts} />);
  };

  sections.forEach((section) => {
    const isGroup = section.multiloadGroupId != null;
    if (isGroup) {
      const anchor = getMultiloadGroupP1Trip(section) || section.trips[0];
      if (anchor) pushLeg(`leg-ml-${section.multiloadGroupId}`, anchor, true);
      const ids = section.trips.map((trip) => trip.trip_id || trip.id).filter(Boolean);
      rows.push(
        <tr key={`ml-${section.multiloadGroupId}`} className="ml-group ml-first">
          <td colSpan={COLS} className="border-b border-black/[0.06] px-2 py-1 text-[11px] tabular-nums text-main-primary">
            <span className="font-semibold">Multiload</span>
            <span className="mx-1.5 text-main-primary/40">·</span>
            {ids.join('  ·  ') || '—'}
          </td>
        </tr>
      );
      section.trips.forEach((trip, index) => {
        count += 1;
        const last = index === section.trips.length - 1;
        const highlighted = Boolean(searchTerm) && tripMatchesTripsPageSearch(trip, searchTerm, spaceTypeMap, timezone);
        rows.push(
          <TripDataRow
            key={trip.trip_id || trip.id || `trip-${count}`}
            trip={trip}
            num={count}
            timezone={timezone}
            spaceTypeMap={spaceTypeMap}
            highlighted={highlighted}
            onViewLogs={onViewLogs}
            canViewLogs={canViewLogs}
            groupPos={last ? 'last' : 'mid'}
            groupSize={section.trips.length}
            groupIndex={index + 1}
          />
        );
      });
      return;
    }

    section.trips.forEach((trip, index) => {
      count += 1;
      if (!trip.is_break) pushLeg(`leg-${trip.trip_id || trip.id || count}`, trip, false);
      const highlighted = Boolean(searchTerm) && tripMatchesTripsPageSearch(trip, searchTerm, spaceTypeMap, timezone);
      rows.push(
        <TripDataRow
          key={trip.trip_id || trip.id || `trip-${index}`}
          trip={trip}
          num={count}
          timezone={timezone}
          spaceTypeMap={spaceTypeMap}
          highlighted={highlighted}
          onViewLogs={onViewLogs}
          canViewLogs={canViewLogs}
        />
      );
    });
  });
  return rows;
}

function FleetBar({ window, driver, timezone }) {
  if (!window) {
    return (
      <tr>
        <td colSpan={COLS} className="border-b border-black/[0.06] bg-[#fff8ef] px-2 py-1 text-[12px] text-[#b25000]">
          No fleet window
        </td>
      </tr>
    );
  }
  const rig = window.rig_no || window.fleet_id || 'Fleet';
  const los = [window.los_title, window.space_type_title ? `(${window.space_type_title})` : ''].filter(Boolean).join(' ');
  const span = `${clock(window.start, timezone)}–${clock(window.end, timezone)}`;
  const back = windowGarageReturn(driver, window);
  const leave = back.leaveMs != null ? clock(new Date(back.leaveMs).toISOString(), timezone) : '';
  const arrive = back.arriveMs != null ? clock(new Date(back.arriveMs).toISOString(), timezone) : '';
  const bits = [
    los,
    span,
    back.miles != null ? `Away ${back.miles.toFixed(1)} mi` : '',
    back.driveMinutes != null ? `ETD ${formatDriveMinutes(back.driveMinutes)}` : '',
    leave ? `Leave ${leave}` : '',
    arrive ? `Garage ${arrive}` : '',
    back.late ? 'After window end' : '',
  ].filter(Boolean);

  return (
    <tr>
      <td colSpan={COLS} className="border-b border-black/[0.06] bg-[#f5f5f7] px-2 py-1 text-[12px] leading-4 text-[#1d1d1f]">
        <span className="font-medium">Rig {rig}</span>
        {bits.length ? <span className="text-[#6e6e73]">{`  ·  ${bits.join('  ·  ')}`}</span> : null}
      </td>
    </tr>
  );
}

const DRIVER_COLUMNS = '1.25rem minmax(11rem, 1.5fr) 9.5rem 4.25rem 4.75rem 4.75rem 4.75rem 5.75rem';
const FLEET_COLUMNS = '1.25rem minmax(11rem, 1.5fr) 4.75rem 4.25rem 4.75rem 4.75rem 4.75rem 5.75rem';

function DriverBar({ driver, timezone, open, onToggle, columns }) {
  const stats = driverStats(driver);
  const fleet = isFleetDriver(driver);
  const when = shiftLabel(driver, timezone);
  const fleets = fleetCount(driver);
  return (
    <button
      type="button"
      aria-expanded={open}
      onClick={onToggle}
      className="grid w-full min-w-0 items-center gap-x-3 overflow-hidden border-b border-white/20 bg-main-primary px-3 py-2 text-left text-white active:bg-white/10"
      style={{ gridTemplateColumns: columns }}
    >
      <ChevronRight
        className={`trip-chevron h-3.5 w-3.5 shrink-0 text-white/80 ${open ? 'rotate-90' : ''}`}
        strokeWidth={2}
        aria-hidden
      />
      <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
        <span className="truncate text-[13px] font-semibold tracking-[-0.014em]">
          {driver.driver_name || 'Driver'}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          <DriverFreezeTypeBadge freezeType={driver.freeze_type} />
          <DriverMultiloadBadge driver={driver} />
        </span>
      </span>
      {fleet ? (
        <span className="flex flex-col items-end leading-none" title={`${fleets} fleet${fleets === 1 ? '' : 's'}`}>
          <span className="text-[10px] font-medium text-white/60">Fleets</span>
          <span className="mt-0.5 text-[13px] font-semibold tabular-nums">{fleets}</span>
        </span>
      ) : (
        <span className="truncate text-[12px] tabular-nums text-white/80" title={when || ''}>
          {when || '—'}
        </span>
      )}
      <span className="text-right text-[12px] font-semibold tabular-nums">{stats.tripCount}</span>
      <span className="text-right text-[12px] font-semibold tabular-nums">{stats.loaded.toFixed(1)}</span>
      <span className="text-right text-[12px] font-semibold tabular-nums text-yellow-300">{stats.deadhead.toFixed(1)}</span>
      <span className={`text-right text-[12px] font-semibold tabular-nums ${stats.away > 0 ? 'text-sky-200' : 'text-white/45'}`}>
        {stats.away > 0 ? stats.away.toFixed(1) : '—'}
      </span>
      <span className="text-right text-[12px] font-semibold tabular-nums">{formatTripRevenue(stats.revenue)}</span>
    </button>
  );
}

export default function TripBucketList({
  drivers,
  timezone,
  spaceTypeMap = {},
  multiloadRoutes = {},
  searchTerm = '',
  onViewLogs,
  canViewLogs = false,
}) {
  const [openIds, setOpenIds] = useState(() => new Set());
  const searching = Boolean(searchTerm);

  const toggle = (id) => {
    if (searching) return;
    setOpenIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fleetList = drivers.length > 0 && drivers.every((driver) => isFleetDriver(driver));
  const columns = fleetList ? FLEET_COLUMNS : DRIVER_COLUMNS;

  return (
    <div className="trip-list h-[calc(100vh-232px)] overflow-auto rounded-lg border border-black/[0.08] bg-[#e7e7ea]">
      <div
        className="trip-list-head sticky top-0 z-20 grid items-center gap-x-3 border-b border-black/[0.08] px-3 py-1.5 text-[11px] font-medium text-[#6e6e73]"
        style={{ gridTemplateColumns: columns }}
      >
        <span />
        <span>Driver</span>
        <span className={fleetList ? 'text-right' : ''}>{fleetList ? 'Fleets' : 'Shift'}</span>
        <span className="text-right">Trips</span>
        <span className="text-right">LM</span>
        <span className="text-right">DH</span>
        <span className="text-right">Away</span>
        <span className="text-right">Rev</span>
      </div>
      {drivers.length === 0 ? (
        <p className="bg-white px-3 py-8 text-center text-[13px] text-[#6e6e73]">No drivers in this view.</p>
      ) : (
        <div className="flex flex-col bg-main-primary">
          {drivers.map((driver) => {
            const id = String(driver.driver_id);
            const open = searching || openIds.has(id);
            const fleet = isFleetDriver(driver);
            const groups = fleet
              ? groupTripsByWindow(driver)
              : [{ window: undefined, trips: sortTripsForDisplay(driver.trips || []) }];

            return (
              <section key={id} className="min-w-0 bg-white">
                <DriverBar
                  driver={driver}
                  timezone={timezone}
                  open={open}
                  onToggle={() => toggle(id)}
                  columns={columns}
                />
                <BucketPanel open={open}>
                  <div className="overflow-x-auto">
                    <table className="w-max min-w-full border-separate border-spacing-0 text-left">
                      <thead>
                        <tr>
                          {HEADERS.map((label) => (
                            <th key={label} className={`${thClass} bg-[#f5f5f7]`}>{label}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {groups.map((group) => {
                          const sections = buildTripSections(
                            fleet ? sortTripsForDisplay(group.trips || []) : group.trips
                          );
                          const windowKey = group.window === undefined
                            ? 'driver'
                            : (group.window == null ? 'none' : group.window.window_index);
                          return (
                            <React.Fragment key={`${id}-${windowKey}`}>
                              {fleet ? (
                                <FleetBar window={group.window} driver={driver} timezone={timezone} />
                              ) : null}
                              {sections.length
                                ? sectionRows({
                                  sections,
                                  timezone,
                                  spaceTypeMap,
                                  driverTrips: driver.trips,
                                  multiloadRoutes,
                                  searchTerm,
                                  onViewLogs,
                                  canViewLogs,
                                })
                                : (
                                  <tr>
                                    <td colSpan={COLS} className="border-b border-black/[0.06] px-2 py-1.5 text-[12px] text-[#6e6e73]">
                                      No trips in this window
                                    </td>
                                  </tr>
                                )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </BucketPanel>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}
